/* assets/bundle_hydrator.js
   Hydrates bundle contents in cart/drawer AND injects a preorder note for parent-only bundles.
   This version:
     • places the preorder note UNDER the bundle pills (inside the bundle block)
     • uses Storefront API to detect continue-selling-when-OOS via `currentlyNotInStock`
     • if ANY component is currentlyNotInStock, injects preorder note on the bundle
     • removes bold text and colors the note #EA6739
     • keeps existing proportional price allocation + rendering
   Requirements:
     • Expose your Storefront token as window.G_TOK
       e.g. in theme.liquid: <script>window.G_TOK = "xxxx";</script>
*/
(function () {
  // ================= MANUAL SETTINGS =================
  // Ship date text for the preorder note
  const PREORDER_SHIP_DATE = 'March 25th';

  // Note color (no bold)
  const NOTE_COLOR_HEX = '#EA6739';

  // Debug: add ?debugCart=1 (or ?debug=1) once and it will persist while you browse
  // ===================================================

  // ---------- DEBUG GATE (case-insensitive + persistent) ----------
  const dbgParam = new URLSearchParams(location.search).toString().toLowerCase();
  const dbgNow = dbgParam.includes('debugcart') || dbgParam.includes('debug=1');
  if (dbgNow) { try { localStorage.setItem('bh_debug', '1'); } catch {} }
  const shouldDebug = () => {
    try { return localStorage.getItem('bh_debug') === '1'; } catch { return dbgNow; }
  };
  const log = (...args) => { if (shouldDebug()) console.log('[BundleHydrator]', ...args); };

  // ---------- UTILS ----------
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const asNumber = (v, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };

  // Money helpers
  const ROUND_TO_CENTS = 100;          // 100 = $1 increments (change to 1 for cents)
  const ROUND_CHEAPEST_FIRST = true;   // fix rounding drift touching cheapest first
  const roundCents = (n) => Math.round(n / ROUND_TO_CENTS) * ROUND_TO_CENTS;
  const moneyNoCents = (cents) => `$${Math.round((Number(cents) || 0) / 100)}`; // "$12"

  // ---------- DETAILS PARSER ----------
  // Tries JSON first (e.g., [{"id":"gid://shopify/ProductVariant/123","qty":1}, ...]),
  // then falls back to delimited text accepting GraphQL GIDs or bare numeric IDs.
  const parseDetails = (str) => {
    if (!str) return [];
    // JSON first
    try {
      const json = JSON.parse(str);
      const arr = Array.isArray(json) ? json : (json.items || []);
      if (Array.isArray(arr) && arr.length) {
        const out = arr.map(x => {
          const raw = String(x.id || x.variant_id || '').trim();
          const gid = raw.match(/ProductVariant\/(\d{6,})/i);
          const id  = gid ? gid[1] : (raw.match(/\d{6,}/) ? raw.match(/\d{6,}/)[0] : null);
          const qty = Number(x.qty || x.quantity || 1) || 1;
          return id ? { id, qty: Math.max(1, qty) } : null;
        }).filter(Boolean);
        if (out.length) return out;
      }
    } catch { /* not JSON; continue */ }

    // Delimited fallback
    const cleaned = String(str).replace(/&amp;/g, '&').trim();
    const chunks = cleaned.split(/[,;|\n]+/);
    const items = [];
    for (const raw of chunks) {
      const chunk = raw.trim();
      if (!chunk) continue;
      const gidMatch = chunk.match(/gid:\/\/shopify\/ProductVariant\/(\d{6,})/i);
      const idMatch  = chunk.match(/(^|\D)(\d{6,})(\D|$)/);
      const id = gidMatch ? gidMatch[1] : (idMatch ? idMatch[2] : null);
      const qtyMatch = chunk.match(/(?:x|×|:|-)\s*(\d+)/i);
      const qty = qtyMatch ? Math.max(1, parseInt(qtyMatch[1], 10)) : 1;
      if (id) items.push({ id, qty });
    }
    return items;
  };

  // ---------- DATA FETCH (variant JSON for image/name/price) ----------
  const fetchVariant = (id) =>
    fetch(`/variants/${id}.js`, { headers: { 'Accept': 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`Variant ${id} not found`))));

  const imgSrc = (v) => {
    if (v.featured_image) {
      if (typeof v.featured_image === 'string') return v.featured_image;
      if (v.featured_image.src) return v.featured_image.src;
    }
    if (v.image) {
      if (typeof v.image === 'string') return v.image;
      if (v.image.src) return v.image.src;
    }
    return '';
  };

  // ---------- STOREFRONT FLAGS (currentlyNotInStock) ----------
  const STOREFRONT_ENDPOINT = '/api/2025-01/graphql.json';
  const sfFlagsCache = new Map(); // key: numeric variant id -> { availableForSale, currentlyNotInStock }

  const fetchStorefrontFlags = async (variantNumericId) => {
    if (sfFlagsCache.has(variantNumericId)) return sfFlagsCache.get(variantNumericId);

    const token = (window.G_TOK || '').trim();
    if (!token) {
      const fallback = { availableForSale: null, currentlyNotInStock: null, error: 'missing_token' };
      sfFlagsCache.set(variantNumericId, fallback);
      return fallback;
    }

    const gid = `gid://shopify/ProductVariant/${variantNumericId}`;
    const query = `
      query VariantFlags($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            availableForSale
            currentlyNotInStock
          }
        }
      }
    `;

    try {
      const res = await fetch(STOREFRONT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query, variables: { id: gid } })
      });

      const json = await res.json();
      const node = json?.data?.node || null;

      const out = {
        availableForSale: node ? !!node.availableForSale : null,
        currentlyNotInStock: node ? !!node.currentlyNotInStock : null
      };

      sfFlagsCache.set(variantNumericId, out);
      return out;
    } catch (e) {
      const out = { availableForSale: null, currentlyNotInStock: null, error: String(e && e.message ? e.message : e) };
      sfFlagsCache.set(variantNumericId, out);
      return out;
    }
  };

  // Decides preorder based on Storefront currentlyNotInStock.
  // If Storefront fails/missing token, we fall back to the old heuristic using /variants/{id}.js.
  const isPreorderish = (variantJs, sfFlags) => {
    // Primary: Storefront `currentlyNotInStock`
    if (sfFlags && sfFlags.currentlyNotInStock === true) {
      log('variant preorder (storefront)', { id: variantJs.id, currentlyNotInStock: true });
      return true;
    }

    // If storefront explicitly says false, then it's not preorder by this rule.
    if (sfFlags && sfFlags.currentlyNotInStock === false) {
      log('variant preorder (storefront)', { id: variantJs.id, currentlyNotInStock: false });
      return false;
    }

    // Fallback: old heuristic (useful if token missing or Storefront is blocked)
    const invMgmt   = variantJs.inventory_management || null;                 // "shopify" | null
    const qtyRaw    = (variantJs.inventory_quantity ?? null);                 // may be null/hidden
    const hasQty    = qtyRaw !== null && qtyRaw !== undefined && Number.isFinite(Number(qtyRaw));
    const qty       = hasQty ? Number(qtyRaw) : null;
    const available = !!variantJs.available;

    log('variant check (fallback)', { id: variantJs.id, invMgmt, qty, available, sfFlags });

    if (invMgmt && hasQty) return qty <= 0;
    if (available === false) return true;
    return false;
  };

  // Insert note **inside** the bundle block, *before* the pills list
  const injectPreorderNote = (wrap) => {
    if (wrap.dataset.preorderNoteInjected === '1') return;
    wrap.dataset.preorderNoteInjected = '1';

    const list = wrap.querySelector('.bundle-contents__list');

    const note = document.createElement('p');
    note.className = 'cart-item-preorder fs-body-60';
    note.style.marginTop = '0.5rem';
    note.style.marginBottom = '0.5rem';   // adds breathing room before the pills
    note.style.color = NOTE_COLOR_HEX;
    note.style.fontWeight = 'normal';
    note.style.lineHeight = '1.4';
    note.textContent = `Preorder: Due to high demand order will ship ${PREORDER_SHIP_DATE}`;

    // Place ABOVE the pills list
    if (list && list.parentElement) {
      list.parentElement.insertBefore(note, list);
    } else {
      // Fallback: put it at the top of the wrap
      wrap.insertBefore(note, wrap.firstChild);
    }

    log('preorder note injected above pills');
  };

  // Inject note ABOVE the preorder marker for normal items
  const injectPreorderNoteAboveMarker = (markerEl) => {
    if (!markerEl || markerEl.dataset.preorderNoteInjected === '1') return;
    markerEl.dataset.preorderNoteInjected = '1';

    const note = document.createElement('p');
    note.className = 'cart-item-preorder fs-body-60';
    note.style.marginTop = '0.5rem';
    note.style.marginBottom = '0.5rem';
    note.style.color = NOTE_COLOR_HEX;
    note.style.fontWeight = 'normal';
    note.style.lineHeight = '1.4';
    note.textContent = `Preorder: Order will Ship ${PREORDER_SHIP_DATE}`;

    markerEl.parentElement.insertBefore(note, markerEl);

    log('normal-item preorder note injected', { variantId: markerEl.dataset.variantId });
  };



  // ---------- RENDER ----------
  const renderItem = (container, v, qty, unitAllocCents, originalUnitCents) => {
    const li = document.createElement('li');
    li.className = 'bundle-contents__item bundle-chip';

    const productName  = (v.name || '').split(' - ')[0] || v.name || 'Item';
    const variantTitle = (v.title && v.title !== 'Default Title') ? ` — ${v.title}` : '';
    const image = imgSrc(v);

    const current  = Number(unitAllocCents) || 0;
    const original = Number(originalUnitCents);
    const showCompare = Number.isFinite(original) && original > current;

    li.innerHTML = `
      <figure class="bundle-chip__img">
        ${image ? `<img src="${image}${image.includes('?') ? '&' : '?'}width=80" alt="${productName}${variantTitle}" />` : ``}
      </figure>

      <span class="bundle-chip__title" title="${productName}${variantTitle}">
        ${productName}${variantTitle}
      </span>

      <span class="bundle-chip__price-wrap">
        <span class="bundle-chip__qty">×${qty}</span>
        ${showCompare ? `<span class="bundle-chip__compare">${moneyNoCents(original)}</span>` : ``}
        <span class="bundle-chip__price">${moneyNoCents(current)}</span>
      </span>
    `;
    container.appendChild(li);
  };

  // ---------- HYDRATE ----------
  const hydrate = async (wrap) => {
    const list = wrap.querySelector('.bundle-contents__list');
    if (!list) return;
    if (wrap.dataset.hydrated === '1') return; // hydrate once per render
    wrap.dataset.hydrated = '1';
    list.innerHTML = '';

    const pairs = parseDetails(wrap.dataset.bundleDetails || '');
    log('details parsed', pairs);
    if (!pairs.length) { log('no variant IDs parsed; aborting hydrate'); return; }

    // For ONE bundle (not count of bundles)
    const lineFinal = parseInt(wrap.dataset.bundleFinal || '0', 10) || 0;
    const lineQty   = Math.max(1, parseInt(wrap.dataset.bundleQty || '1', 10));
    const perBundleFinal = Math.round(lineFinal / lineQty);
    log('bundle totals', { lineFinal, lineQty, perBundleFinal });

    // Fetch variants from /variants/{id}.js
    const results = await Promise.all(pairs.map(async ({ id, qty }) => {
      try {
        const v = await fetchVariant(id);
        log('fetched variant', id, v);
        return { ok: true, v, qty };
      } catch (e) {
        console.warn('[BundleHydrator] fetch failed', id, e);
        return { ok: false, id, qty };
      }
    }));

    // Fetch Storefront flags (currentlyNotInStock) for OK variants
    const items = await Promise.all(results.map(async (r) => {
      if (!r.ok) return r;

      const sf = await fetchStorefrontFlags(String(r.v.id));
      log('storefront flags', { id: r.v.id, ...sf });

      return {
        ok: true,
        v: r.v,
        qty: r.qty,
        unitPrice: asNumber(r.v.price, 0),               // cents
        unitCompare: asNumber(r.v.compare_at_price, 0),  // cents (display-only)
        preorderish: isPreorderish(r.v, sf)
      };
    }));

    // Preorder: if ANY component is currentlyNotInStock (or fallback says preorderish),
    // inject a note UNDER the pills for the bundle.
    const anyComponentPreorder = items.some(it => it.ok && it.preorderish);
    log('anyComponentPreorder', anyComponentPreorder);
    if (anyComponentPreorder) injectPreorderNote(wrap);

    const priceSum = items.reduce((s, it) => it.ok ? s + it.unitPrice * it.qty : s, 0);
    if (!(perBundleFinal > 0 && priceSum > 0)) {
      log('allocation skipped; render raw items', { priceSum, perBundleFinal });
      for (const it of items) {
        if (!it.ok) {
          const li = document.createElement('li');
          li.className = 'bundle-contents__item';
          li.textContent = `Item ${it.id} × ${it.qty}`;
          list.appendChild(li);
        } else {
          const displayOriginal = it.unitCompare > it.unitPrice ? it.unitCompare : it.unitPrice;
          renderItem(list, it.v, it.qty, it.unitPrice, displayOriginal);
        }
      }
      return;
    }

    // Proportional allocation (per-unit)
    const ratio = perBundleFinal / priceSum;
    const work = items.filter(it => it.ok).map((it, idx) => ({
      ...it,
      idx,
      rawAlloc: it.unitPrice * ratio
    }));

    // Rounding
    if (ROUND_TO_CENTS) {
      const order = [...work].sort((a, b) =>
        ROUND_CHEAPEST_FIRST ? (a.unitPrice - b.unitPrice || a.idx - b.idx) : (a.idx - b.idx)
      );
      order.forEach(it => { it.unitAlloc = roundCents(it.rawAlloc); });

      // Fix rounding drift
      const currentTotal = order.reduce((s, it) => s + it.unitAlloc * it.qty, 0);
      const diff = perBundleFinal - currentTotal;
      if (diff) {
        const target = order[order.length - 1];
        target.unitAlloc = Math.max(0, target.unitAlloc + Math.round(diff / target.qty));
      }
    } else {
      work.forEach(it => { it.unitAlloc = Math.round(it.rawAlloc); });
      const subtotal = work.reduce((s, it) => s + it.unitAlloc * it.qty, 0);
      const diff = perBundleFinal - subtotal;
      if (diff) {
        const last = work[work.length - 1];
        last.unitAlloc = Math.max(0, last.unitAlloc + Math.round(diff / last.qty));
      }
    }

    // Render in original order
    const byIdx = new Map(work.map(it => [it.idx, it]));
    for (let i = 0; i < items.length; i++) {
      const base = items[i];
      if (!base.ok) {
        const li = document.createElement('li');
        li.className = 'bundle-contents__item';
        li.textContent = `Item ${base.id} × ${base.qty}`;
        list.appendChild(li);
        continue;
      }
      const it = byIdx.get(i);
      const displayOriginal = base.unitCompare > base.unitPrice ? base.unitCompare : base.unitPrice;
      renderItem(list, base.v, base.qty, it.unitAlloc, displayOriginal);
    }
  };

  const hydrateNormalItems = async (scope = document) => {
    const markers = qa('.preorder-check[data-variant-id]', scope);

    for (const marker of markers) {
      // prevent duplicate work across rerenders
      if (marker.dataset.checked === '1') continue;
      marker.dataset.checked = '1';

      const variantId = (marker.dataset.variantId || '').trim();
      if (!variantId) continue;

      const sf = await fetchStorefrontFlags(variantId);
      log('normal-item storefront flags', { id: variantId, ...sf });

      if (sf.currentlyNotInStock === true) {
        injectPreorderNoteAboveMarker(marker);
      }
    }
  };


  // ---------- BOOT + INTEGRATIONS ----------
  const run = (scope = document) => {
    if (shouldDebug()) console.log('[BundleHydrator] run()', scope === document ? 'document' : scope);

    // Bundles
    qa('.bundle-contents[data-bundle-details]', scope).forEach(hydrate);

    // Normal items
    hydrateNormalItems(scope);
  };

  const ready = () => run();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }

  // Shopify section lifecycle (theme editor + dynamic sections)
  ['shopify:section:load','shopify:section:reorder','shopify:section:select','shopify:section:deselect']
    .forEach(evt => document.addEventListener(evt, () => run()));

  // Common cart/drawer events across themes
  ['cart:open','cart:rendered','cart:updated','ajaxCart:afterCartLoad','cart:change','cart:refresh']
    .forEach(evt => document.addEventListener(evt, () => run()));

  // Observe the drawer container specifically (when present)
  const drawer =
    document.querySelector('[data-cart-drawer]') ||
    document.querySelector('.cart-drawer') ||
    document.querySelector('cart-drawer');

  if (drawer) {
    new MutationObserver(() => run(drawer))
      .observe(drawer, { childList: true, subtree: true });
  }

  // Fallback: observe the whole document for swapped cart HTML
  new MutationObserver((muts) => {
    if (muts.some(m => m.addedNodes && m.addedNodes.length)) run();
  }).observe(document.body, { childList: true, subtree: true });

  // Optional heartbeat while drawer is open (debug only)
  if (shouldDebug()) {
    setInterval(() => {
      const openDrawer = document.querySelector('[data-cart-drawer].is-open, .cart-drawer.is-open, cart-drawer[open]');
      if (openDrawer) run(openDrawer);
    }, 1200);
  }

  // Public debug hook
  window.BundleHydrator = window.BundleHydrator || {};
  window.BundleHydrator.run = () => run();
})();
